/**
 * Tool Definitions for the AI Agent
 * Defines the function-calling schemas that tell DeepSeek what tools are available.
 * Each tool definition uses JSON Schema for parameters.
 */

import type { ToolDefinition } from './provider';

/**
 * Tools that read data - these are SAFE and execute automatically
 */
export const readTools: ToolDefinition[] = [
  {
    type: 'function',
    function: {
      name: 'get_dashboard_kpis',
      description: 'Get the current user\'s dashboard KPIs (key performance indicators) and financial metrics',
      parameters: { type: 'object', properties: {} },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_records',
      description: 'List all accounting records for the current user. Use this to see what records the user has access to.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'Optional: filter by specific record ID' },
        },
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_transactions',
      description: 'Get transactions for a specific record with optional filters. The record_id is required.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID to fetch transactions for' },
          start_date: { type: 'string', description: 'Start date filter (YYYY-MM-DD format)' },
          end_date: { type: 'string', description: 'End date filter (YYYY-MM-DD format)' },
          transaction_action_id: { type: 'number', description: 'Filter by action entity ID' },
          transaction_object_id: { type: 'number', description: 'Filter by object entity ID' },
          transaction_media_id: { type: 'number', description: 'Filter by media entity ID' },
          amount_min: { type: 'number', description: 'Minimum amount filter' },
          amount_max: { type: 'number', description: 'Maximum amount filter' },
          quantity_min: { type: 'number', description: 'Minimum quantity filter' },
          quantity_max: { type: 'number', description: 'Maximum quantity filter' },
          page: { type: 'number', description: 'Page number for pagination' },
          limit: { type: 'number', description: 'Items per page' },
        },
        required: ['record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_transaction_entities',
      description: 'Get transaction entities (actions, objects, indirect objects, media) for a specific record. The record_id is required.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'REQUIRED: The record ID to get entities for' },
          category: { type: 'string', enum: ['action', 'object', 'indirect_object', 'media'], description: 'Filter by entity category' },
          name: { type: 'string', description: 'Filter by entity name (partial match)' },
          type: { type: 'string', description: 'Filter by entity type (credit, debit, tradable, etc.)' },
          linked_to_kpi: { type: 'boolean', description: 'Filter by KPI linkage' },
          page: { type: 'number', description: 'Page number' },
          limit: { type: 'number', description: 'Items per page' },
        },
        required: ['record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_ledgers',
      description: 'Get ledger accounts for a record. Shows account balances for each entity.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          start_date: { type: 'string', description: 'Start date (YYYY-MM-DD)' },
          end_date: { type: 'string', description: 'End date (YYYY-MM-DD)' },
          page: { type: 'number', description: 'Page number' },
        },
        required: ['record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_ledger_detail',
      description: 'Get detailed ledger view for a specific entity, showing debit and credit transactions.',
      parameters: {
        type: 'object',
        properties: {
          entity_id: { type: 'number', description: 'The entity ID to get ledger for' },
          record_id: { type: 'number', description: 'The record ID' },
          start_date: { type: 'string', description: 'Start date (YYYY-MM-DD) - REQUIRED' },
          end_date: { type: 'string', description: 'End date (YYYY-MM-DD) - REQUIRED' },
        },
        required: ['entity_id', 'record_id', 'start_date', 'end_date'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_balance_sheet',
      description: 'Get balance sheet showing assets, liabilities, and capital for a date range.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          start_date: { type: 'string', description: 'Start date (YYYY-MM-DD) - REQUIRED' },
          end_date: { type: 'string', description: 'End date (YYYY-MM-DD) - REQUIRED' },
        },
        required: ['record_id', 'start_date', 'end_date'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_profit_loss',
      description: 'Get profit and loss statement showing debits (expenses) and credits (revenues) for a date range.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          start_date: { type: 'string', description: 'Start date (YYYY-MM-DD) - REQUIRED' },
          end_date: { type: 'string', description: 'End date (YYYY-MM-DD) - REQUIRED' },
        },
        required: ['record_id', 'start_date', 'end_date'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_stock_valuation',
      description: 'Get stock valuation accounts showing inventory items with their current valuations.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          page: { type: 'number', description: 'Page number' },
        },
        required: ['record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_trading_accounts',
      description: 'Get trading accounts list with their balances.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          page: { type: 'number', description: 'Page number' },
        },
        required: ['record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_credit_balance',
      description: 'Get the current user\'s credit points balance.',
      parameters: { type: 'object', properties: {} },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_credit_transactions',
      description: 'Get credit point transaction history for the current user.',
      parameters: {
        type: 'object',
        properties: {
          type: { type: 'string', description: 'Filter by transaction type' },
        },
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_subscription',
      description: 'Get the current user\'s subscription details and status.',
      parameters: { type: 'object', properties: {} },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_plans',
      description: 'Get all available subscription plans with their features and pricing.',
      parameters: { type: 'object', properties: {} },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_notifications',
      description: 'Get all notifications for the current user.',
      parameters: { type: 'object', properties: {} },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_record_users',
      description: 'Get users who have access to a specific record, with their permissions.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
        },
        required: ['record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_entities_by_similarity',
      description: 'Search for transaction entities using semantic similarity matching. Useful for finding entities by meaning rather than exact name.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'The search query text to find similar entities' },
          category: { type: 'string', enum: ['action', 'object', 'indirect_object', 'media'], description: 'Optional: filter by entity category' },
          limit: { type: 'number', description: 'Maximum number of results' },
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_overall_stock_valuation',
      description: 'Get overall (aggregated) stock valuation across all stock items for a date range.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          start_date: { type: 'string', description: 'Start date (YYYY-MM-DD) - REQUIRED' },
          end_date: { type: 'string', description: 'End date (YYYY-MM-DD) - REQUIRED' },
        },
        required: ['record_id', 'start_date', 'end_date'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_overall_trading_account',
      description: 'Get overall (aggregated) trading account with combined debit/credit balances for a date range.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          start_date: { type: 'string', description: 'Start date (YYYY-MM-DD) - REQUIRED' },
          end_date: { type: 'string', description: 'End date (YYYY-MM-DD) - REQUIRED' },
        },
        required: ['record_id', 'start_date', 'end_date'],
      },
    },
  },
];

/**
 * Tools that perform actions - these REQUIRE user confirmation before execution
 */
export const actionTools: ToolDefinition[] = [
  {
    type: 'function',
    function: {
      name: 'create_transaction',
      description: 'Create a new transaction in a record. This will modify financial data.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          transaction_date: { type: 'string', description: 'Date of transaction (YYYY-MM-DD HH:mm:ss format)' },
          transaction_action_id: { type: 'number', description: 'The action entity ID (what happened)' },
          transaction_object_id: { type: 'number', description: 'The object entity ID (what was affected)' },
          transaction_indirect_object_id: { type: 'number', description: 'Optional: indirect object entity ID' },
          transaction_media_id: { type: 'number', description: 'The media/payment entity ID' },
          transaction_quantity: { type: 'number', description: 'Quantity of units' },
          transaction_total_amount: { type: 'number', description: 'Total monetary amount' },
        },
        required: ['record_id', 'transaction_date', 'transaction_action_id', 'transaction_object_id', 'transaction_media_id', 'transaction_quantity', 'transaction_total_amount'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'update_transaction',
      description: 'Update an existing event. Prefer existing terms. Only include transaction_date if the user asked to change when it happened; otherwise keep the existing date from get_transactions.',
      parameters: {
        type: 'object',
        properties: {
          transaction_id: { type: 'number', description: 'The transaction ID to update' },
          record_id: { type: 'number', description: 'The record ID' },
          transaction_date: { type: 'string', description: 'Only if changing the date (YYYY-MM-DD HH:mm:ss). Otherwise omit and reuse the existing date from get_transactions.' },
          transaction_action_id: { type: 'number', description: 'New action entity ID' },
          transaction_object_id: { type: 'number', description: 'New object entity ID' },
          transaction_indirect_object_id: { type: 'number', description: 'Optional: new indirect object entity ID' },
          transaction_media_id: { type: 'number', description: 'New media entity ID' },
          transaction_quantity: { type: 'number', description: 'New quantity' },
          transaction_total_amount: { type: 'number', description: 'New total amount' },
        },
        required: ['transaction_id', 'record_id', 'transaction_action_id', 'transaction_object_id', 'transaction_media_id', 'transaction_quantity', 'transaction_total_amount'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'delete_transaction',
      description: 'Delete a transaction from a record. This will permanently remove financial data.',
      parameters: {
        type: 'object',
        properties: {
          transaction_id: { type: 'number', description: 'The transaction ID to delete' },
          record_id: { type: 'number', description: 'The record ID this transaction belongs to' },
        },
        required: ['transaction_id', 'record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'create_record',
      description: 'Create a new accounting record (like a company book).',
      parameters: {
        type: 'object',
        properties: {
          record_name: { type: 'string', description: 'Name for the new record' },
          record_description: { type: 'string', description: 'Optional description' },
          record_currency_code: { type: 'string', description: 'Currency code (e.g., "USD", "EUR", "GHS")' },
        },
        required: ['record_name', 'record_currency_code'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'update_record',
      description: 'Update an existing record\'s name, description, or currency.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID to update' },
          record_name: { type: 'string', description: 'New name for the record' },
          record_description: { type: 'string', description: 'New description' },
          record_currency_code: { type: 'string', description: 'New currency code' },
        },
        required: ['record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'delete_record',
      description: 'Delete books. Requires a verification code sent to the owner\'s email. Include record_name for the confirm card.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The books ID to delete' },
          creator_id: { type: 'number', description: 'The creator/user ID of the books owner' },
          verification_code: { type: 'string', description: 'The verification code sent to the owner\'s email' },
          record_name: { type: 'string', description: 'Name of the books (for confirmation display)' },
        },
        required: ['record_id', 'creator_id', 'verification_code'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'create_entity',
      description: 'Create a new term ONLY when get_transaction_entities or search_entities found no suitable match. Prefer reusing existing terms.',
      parameters: {
        type: 'object',
        properties: {
          transaction_entity_name: { type: 'string', description: 'Name of the term (e.g., "Sold", "Product A", "Cash")' },
          transaction_entity_description: { type: 'string', description: 'Optional description' },
          transaction_entity_category: { type: 'string', enum: ['action', 'object', 'indirect_object', 'media'], description: 'Term category' },
          transaction_entity_type: { type: 'string', enum: ['credit', 'debit', 'tradable', 'non_tradable', 'payable', 'third_party'], description: 'Optional type' },
          transaction_entity_past_tense: { type: 'string', description: 'Optional past tense for actions (e.g., "Sold" for "Sell")' },
        },
        required: ['transaction_entity_name', 'transaction_entity_category'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'update_entity',
      description: 'Update an existing transaction entity (rename, change type, etc.). Always include record_id from the selected books.',
      parameters: {
        type: 'object',
        properties: {
          entity_id: { type: 'number', description: 'The entity ID to update' },
          record_id: { type: 'number', description: 'REQUIRED: The selected books/record ID' },
          transaction_entity_name: { type: 'string', description: 'New name' },
          transaction_entity_description: { type: 'string', description: 'New description' },
          transaction_entity_category: { type: 'string', enum: ['action', 'object', 'indirect_object', 'media'], description: 'New category' },
          transaction_entity_type: { type: 'string', description: 'New type' },
          transaction_entity_past_tense: { type: 'string', description: 'New past tense' },
        },
        required: ['entity_id', 'record_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'delete_entity',
      description: 'Delete a term. Include the name and category so the confirm card can label it clearly.',
      parameters: {
        type: 'object',
        properties: {
          entity_id: { type: 'number', description: 'The term ID to delete' },
          transaction_entity_name: { type: 'string', description: 'Name of the term (for confirmation display)' },
          transaction_entity_category: { type: 'string', enum: ['action', 'object', 'indirect_object', 'media'], description: 'Category (for confirmation display)' },
        },
        required: ['entity_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'join_record',
      description: 'Join a record using an invite code.',
      parameters: {
        type: 'object',
        properties: {
          code: { type: 'string', description: 'The invite/join code' },
        },
        required: ['code'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'grant_permission',
      description: 'Grant or update permissions for a user on a record.',
      parameters: {
        type: 'object',
        properties: {
          record_id: { type: 'number', description: 'The record ID' },
          target_user_id: { type: 'number', description: 'The target user ID to grant permission to' },
          can_read: { type: 'boolean', description: 'Whether the user can read data' },
          can_write: { type: 'boolean', description: 'Whether the user can write/edit data' },
        },
        required: ['record_id', 'target_user_id', 'can_read', 'can_write'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'update_profile',
      description: 'Update the user\'s profile information (username, first name, last name, email, etc.).',
      parameters: {
        type: 'object',
        properties: {
          first_name: { type: 'string', description: 'New first name' },
          last_name: { type: 'string', description: 'New last name' },
          username: { type: 'string', description: 'New username' },
        },
      },
    },
  },
];

/**
 * Tools that don't modify data but are still actions - no confirmation needed
 */
export const utilityTools: ToolDefinition[] = [
  {
    type: 'function',
    function: {
      name: 'navigate_to_page',
      description: 'Navigate the user to a specific page in the application. Use this when the user asks to go somewhere.',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'The route path to navigate to (e.g., "/dashboard", "/records/transactions")' },
          page_name: { type: 'string', description: 'A human-readable name for the destination page' },
        },
        required: ['path', 'page_name'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'go_to_profile',
      description: 'Navigate the user to their profile/settings page where they can edit their account details.',
      parameters: {
        type: 'object',
        properties: {},
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'toggle_dark_mode',
      description: 'Switch between light and dark mode (theme) in the application.',
      parameters: {
        type: 'object',
        properties: {},
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'download_apk',
      description: 'Provide the user with a link to download the Balancio Pro Android APK file.',
      parameters: {
        type: 'object',
        properties: {},
      },
    },
  },
];

/**
 * Knowledge tools - these handle learning and knowledge retrieval
 */
export const knowledgeTools: ToolDefinition[] = [
  {
    type: 'function',
    function: {
      name: 'store_lesson',
      description: 'Save a lesson, tip, or important information that the user has shared so it can be remembered for future conversations.',
      parameters: {
        type: 'object',
        properties: {
          topic: { type: 'string', description: 'A topic/keyword for this lesson' },
          content: { type: 'string', description: 'The lesson content to remember' },
        },
        required: ['topic', 'content'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'retrieve_lessons',
      description: 'Retrieve previously stored lessons that are relevant to the current conversation context.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'A search query to find relevant lessons' },
        },
        required: ['query'],
      },
    },
  },
];

/**
 * Refresh / lifecycle tools
 * These allow the AI to control UI behavior after tool execution
 */
export const lifecycleTools: ToolDefinition[] = [
  {
    type: 'function',
    function: {
      name: 'refresh_page',
      description: 'Refresh/reload the current page data by navigating to the same route. Use this AFTER creating, updating, or deleting data so the user can see the latest changes reflected on screen.',
      parameters: {
        type: 'object',
        properties: {
          message: {
            type: 'string',
            description: 'A brief message explaining to the user why the page is being refreshed (e.g., "Refreshing to show your new transaction")',
          },
        },
        required: ['message'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'stop_chain',
      description: 'Stop the tool execution chain and do NOT send tool results back to the AI for further processing. Use this when you have already completed the task and want to end the conversation turn gracefully without the AI generating an additional response. The AI will NOT respond after this tool.',
      parameters: {
        type: 'object',
        properties: {
          message: {
            type: 'string',
            description: 'A final message to display to the user (e.g., "Done! Your transaction has been created and the page has been refreshed.")',
          },
        },
        required: ['message'],
      },
    },
  },
];

/**
 * All tools combined
 */
export const allTools: ToolDefinition[] = [
  ...readTools,
  ...actionTools,
  ...knowledgeTools,
  ...lifecycleTools.filter((t) => t.function.name === 'stop_chain'),
];

/**
 * Names of tools that require user confirmation
 */
export const toolsRequiringConfirmation: string[] = actionTools.map(
  t => t.function.name
);

export function toolRequiresConfirmation(toolName: string): boolean {
  return toolsRequiringConfirmation.includes(toolName);
}

export function findToolDefinition(toolName: string): ToolDefinition | undefined {
  return allTools.find(t => t.function.name === toolName);
}
